const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const hasValue = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(parseFloat(v));

/**
 * Quantity of an invoice line in the product's BASE unit.
 * Costs are per base unit, so cost = cost per base unit x base quantity.
 * quantity is kept current after returns; qty_base is not, so derive from quantity.
 */
export function itemBaseQuantity(item) {
  const qty = num(item.quantity);
  const factor = num(item.conversion_to_base) || 1;
  return qty * factor;
}

/** A line billed at 0% GST must not have GST taken out of sales. */
export function itemHasGst(item) {
  return num(item && item.gst_rate) > 0.0001 && num(item && item.gst_amount) > 0.0001;
}

/** True when this invoice was actually billed with GST. */
export function invoiceHasGst(invoice) {
  const items = (invoice && invoice.items) || [];
  if (items.length && items.every((item) => !itemHasGst(item) && num(item.gst_rate) <= 0)) {
    return false;
  }
  if (items.some(itemHasGst)) return true;
  return num(invoice && invoice.tax_amount) > 0.0001
    || num(invoice && invoice.cgst_amount) > 0.0001
    || num(invoice && invoice.sgst_amount) > 0.0001;
}

/**
 * Revenue of a line. GST is subtracted only when that line's GST % is greater than 0.
 * A 0% line keeps the full billed amount, even if leftover gst_amount or header tax exists.
 */
export function itemNetRevenue(item, invoice) {
  const lineTotal = item.total_price != null && item.total_price !== ''
    ? num(item.total_price)
    : num(item.unit_price) * num(item.quantity) - num(item.discount);
  if (num(item.gst_rate) <= 0) return lineTotal;
  if (invoice && !invoiceHasGst(invoice)) return lineTotal;
  return lineTotal - num(item.gst_amount);
}

/**
 * Cost of one invoice line.
 * Uses the cost saved on the line when it was sold (cost_price_base). Older lines
 * without it fall back to the product's current cost and are marked estimated.
 */
export function itemCost(item, productCost) {
  const baseQty = itemBaseQuantity(item);
  if (hasValue(item.cost_price_base)) {
    return { cost: num(item.cost_price_base) * baseQty, estimated: false };
  }
  if (item.product_id && hasValue(productCost)) {
    return { cost: num(productCost) * baseQty, estimated: true };
  }
  // Product no longer exists and no cost was saved: profit for this line is overstated
  if (item.product_id) return { cost: 0, estimated: false, missing: true };
  return { cost: 0, estimated: false };
}

/** Cost to use when a line has no sale-time snapshot: oldest remaining FIFO layer, else catalog cost. */
export function productFifoCost(product) {
  const layers = (product && product.price_layers) || [];
  for (let i = 0; i < layers.length; i += 1) {
    const layer = layers[i];
    if (num(layer.qty_remaining) > 0 && hasValue(layer.cost_price)) return num(layer.cost_price);
  }
  return product ? product.cost : undefined;
}

function costMap(products) {
  const map = {};
  (products || []).forEach((p) => {
    if (p.id) map[p.id] = productFifoCost(p);
  });
  return map;
}

/** Revenue, cost and profit of one invoice. null when it has no line items. */
export function invoiceBreakdown(invoice, products) {
  const items = (invoice && invoice.items) || [];
  if (!items.length) return null;
  const costs = costMap(products);
  let revenue = 0;
  let cost = 0;
  let estimated = false;
  let missingCost = false;
  items.forEach((item) => {
    revenue += itemNetRevenue(item, invoice);
    const line = itemCost(item, costs[item.product_id]);
    cost += line.cost;
    if (line.estimated) estimated = true;
    if (line.missing) missingCost = true;
  });
  const discount = num(invoice.discount);
  return { revenue, cost, discount, estimated, missingCost, profit: revenue - discount - cost };
}

/** True for invoices that are real sales (not dummy, not cancelled). */
export function isRealSale(invoice) {
  if (!invoice || Number(invoice.is_dummy)) return false;
  return String(invoice.status || '').toLowerCase() !== 'cancelled';
}

/** Same rule the dashboard uses for its profit total. */
export function isPaidSale(invoice) {
  const status = String((invoice && invoice.status) || '').toLowerCase();
  return isRealSale(invoice) && (status === 'paid' || status === 'completed');
}

/** Profit of a single invoice, or null when it cannot be calculated. */
export function invoiceProfit(invoice, products) {
  const b = invoiceBreakdown(invoice, products);
  return b ? b.profit : null;
}

/** Profit of paid, non-dummy invoices: sales (ex GST) - invoice discounts - cost of goods sold. */
export function calculateProfit(paidInvoices, products) {
  let profit = 0;
  (paidInvoices || []).forEach((invoice) => {
    const b = invoiceBreakdown(invoice, products);
    if (b) profit += b.profit;
  });
  return profit;
}

/**
 * Per-product profit rows (worst first) so bad costs / units are easy to spot.
 * Invoice-level discount is not allocated to products (shown separately).
 */
export function profitByProduct(paidInvoices, products) {
  const productById = {};
  (products || []).forEach((p) => {
    if (p.id) productById[p.id] = p;
  });

  const rows = {};
  let invoiceDiscount = 0;
  (paidInvoices || []).forEach((invoice) => {
    invoiceDiscount += num(invoice.discount);
    (invoice.items || []).forEach((item) => {
      const key = item.product_id || `desc:${item.description || 'Item'}`;
      const product = item.product_id ? productById[item.product_id] : null;
      if (!rows[key]) {
        rows[key] = {
          key,
          name: product?.name || item.description || 'Item',
          unit: product?.unit || '',
          unitPrice: product ? num(product.price) : 0,
          currentCost: product ? num(product.cost) : 0,
          baseQty: 0,
          revenue: 0,
          cost: 0,
          estimated: false,
          missingProduct: !product
        };
      }
      const row = rows[key];
      row.baseQty += itemBaseQuantity(item);
      row.revenue += itemNetRevenue(item, invoice);
      const line = itemCost(item, product ? product.cost : undefined);
      row.cost += line.cost;
      if (line.estimated) row.estimated = true;
    });
  });

  const list = Object.values(rows).map((row) => {
    const unitCost = row.baseQty > 0 ? row.cost / row.baseQty : row.currentCost;
    return {
      ...row,
      unitCost,
      profit: row.revenue - row.cost,
      // cost per base unit higher than selling price per base unit => bad cost or unit mix-up
      costAbovePrice: unitCost > 0 && row.unitPrice > 0 && unitCost > row.unitPrice
    };
  });
  list.sort((a, b) => a.profit - b.profit);
  return { rows: list, invoiceDiscount };
}

/**
 * Stock value at cost: each product's remaining FIFO layers at their own cost,
 * anything not covered by a layer at the product's cost.
 */
export function inventoryValue(products) {
  let total = 0;
  (products || []).forEach((p) => {
    let left = Math.max(num(p.stock_quantity), 0);
    (p.price_layers || []).forEach((layer) => {
      if (left <= 0 || !hasValue(layer.cost_price)) return;
      const q = Math.min(num(layer.qty_remaining), left);
      if (q <= 0) return;
      total += q * num(layer.cost_price);
      left -= q;
    });
    total += left * num(p.cost);
  });
  return total;
}

/** Price of one unit of a product's UOM (its own sale price, else base price x conversion). */
export function uomPrice(uom, product) {
  if (uom && hasValue(uom.sale_price) && num(uom.sale_price) > 0) return num(uom.sale_price);
  return num(product && product.price) * (num(uom && uom.conversion_to_base) || 1);
}

const relDiff = (a, b) => (b > 0 ? Math.abs(a - b) / b : Infinity);
const UNIT_TOLERANCE = 0.35;

/**
 * Invoice lines that look like they were billed in one unit but saved in another,
 * e.g. packets sold at packet prices but stored as boxes (so a whole box left stock
 * and a whole box of cost was charged). Suggests the unit whose price matches the bill.
 */
export function findUnitMismatches(invoices, products) {
  const productById = {};
  (products || []).forEach((p) => {
    if (p.id) productById[p.id] = p;
  });

  const found = [];
  (invoices || []).forEach((invoice) => {
    if (Number(invoice.is_dummy)) return;
    if (String(invoice.status || '').toLowerCase() === 'cancelled') return;
    (invoice.items || []).forEach((item) => {
      const product = item.product_id ? productById[item.product_id] : null;
      const uoms = (product && product.uoms) || [];
      const qty = num(item.quantity);
      if (!product || uoms.length < 2 || qty <= 0) return;

      const netUnit = itemNetRevenue(item, invoice) / qty;
      if (!(netUnit > 0)) return;

      const factor = num(item.conversion_to_base) || 1;
      const current = uoms.find((u) => u.id === item.uom_id)
        || uoms.find((u) => Math.abs(num(u.conversion_to_base) - factor) < 1e-6);
      const currentExpected = current ? uomPrice(current, product) : num(product.price) * factor;
      const currentDiff = relDiff(netUnit, currentExpected);
      if (currentDiff <= UNIT_TOLERANCE) return;

      let best = null;
      uoms.forEach((u) => {
        if (current && u.id === current.id) return;
        const diff = relDiff(netUnit, uomPrice(u, product));
        if (diff <= UNIT_TOLERANCE && (!best || diff < best.diff)) best = { uom: u, diff };
      });
      if (!best) return;

      const newFactor = num(best.uom.conversion_to_base) || 1;
      const unitCost = hasValue(item.cost_price_base) ? num(item.cost_price_base) : num(product.cost);
      const stockBaseReturned = qty * factor - qty * newFactor;
      found.push({
        key: `${invoice.id}:${item.id}`,
        invoice,
        item,
        product,
        currentCode: current ? current.code : (uoms.find((u) => Number(u.is_base)) || {}).code,
        suggested: best.uom,
        stockBaseReturned,
        stockValueReturned: stockBaseReturned * unitCost
      });
    });
  });
  return found;
}
